import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Paper, Typography,
} from '@mui/material';
import { FixedAssetSettingsService } from '../../services/fixedAssetSettings.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { postingAccounts } from '../../utils/accountHierarchy';
import { AuthService } from '../../services/auth.service';
import type { FixedAssetSettings as FixedAssetSettingsData } from '../../interfaces/fixedAsset/fixedAssetSettings.interface';
import { Account } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';

const EMPTY_FORM: FixedAssetSettingsData = {
  institutionId: '',
  defaultAssetAccountId: '',
  defaultAccumulatedDepreciationAccountId: '',
  defaultDepreciationExpenseAccountId: '',
  defaultGainOnDisposalAccountId: '',
  defaultLossOnDisposalAccountId: '',
  defaultOpeningBalanceEquityAccountId: '',
  revaluationReserveAccountId: '',
  impairmentLossAccountId: '',
  accumulatedImpairmentAccountId: '',
  defaultFundingAccountId: '',
};

const FixedAssetSettings: React.FC = () => {
  const toast = useToast();
  const [form, setForm] = useState<FixedAssetSettingsData>(EMPTY_FORM);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setLoading(true);
      const [settings, accountsRes] = await Promise.all([
        FixedAssetSettingsService.getByInstitution(institutionId).catch(() => null),
        AccountService.getLookup(),
      ]);
      setAccounts(accountsRes ?? []);
      if (settings) {
        setForm({
          institutionId: settings.institutionId ?? institutionId,
          defaultAssetAccountId: settings.defaultAssetAccountId ?? '',
          defaultAccumulatedDepreciationAccountId: settings.defaultAccumulatedDepreciationAccountId ?? '',
          defaultDepreciationExpenseAccountId: settings.defaultDepreciationExpenseAccountId ?? '',
          defaultGainOnDisposalAccountId: settings.defaultGainOnDisposalAccountId ?? '',
          defaultLossOnDisposalAccountId: settings.defaultLossOnDisposalAccountId ?? '',
          defaultOpeningBalanceEquityAccountId: settings.defaultOpeningBalanceEquityAccountId ?? '',
          revaluationReserveAccountId: settings.revaluationReserveAccountId ?? '',
          impairmentLossAccountId: settings.impairmentLossAccountId ?? '',
          accumulatedImpairmentAccountId: settings.accumulatedImpairmentAccountId ?? '',
          defaultFundingAccountId: settings.defaultFundingAccountId ?? '',
        });
      }
    } catch (e) {
      toast.showApiError(e, 'Failed to load fixed asset settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setSaving(true);
      await FixedAssetSettingsService.upsert({ ...form, institutionId });
      toast.showSuccess('Fixed asset settings saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save fixed asset settings');
    } finally {
      setSaving(false);
    }
  };

  // Every default is posted to by asset journals, so only posting (leaf) accounts are offered.
  const leaves = postingAccounts(accounts);
  const assetAccounts = leaves.filter(a => a.accountType === 'ASSET');
  const expenseAccounts = leaves.filter(a => a.accountType === 'EXPENSE');
  const revenueAccounts = leaves.filter(a => a.accountType === 'REVENUE');
  const fundingAccounts = leaves.filter(a => a.accountType === 'ASSET' || a.accountType === 'LIABILITY');
  const equityAccounts = leaves.filter(a => a.accountType === 'EQUITY');

  const picker = (
    label: string,
    field: keyof FixedAssetSettingsData,
    options: Account[],
    help: { 'data-help': string },
  ) => (
    <AccountAutocomplete
      {...help}
      accounts={options}
      value={form[field] ?? ''}
      onChange={(accountId) => setForm(f => ({ ...f, [field]: accountId }))}
      label={label}
      placeholder="None"
    />
  );

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 720 }} data-help-page="fixedAssets.set.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Fixed Asset Settings</Typography>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {picker('Default asset account', 'defaultAssetAccountId', assetAccounts, { 'data-help': 'fixedAssets.set.field.asset' })}
        {picker('Default accumulated-depreciation account', 'defaultAccumulatedDepreciationAccountId', assetAccounts, { 'data-help': 'fixedAssets.set.field.accum' })}
        {picker('Default depreciation-expense account', 'defaultDepreciationExpenseAccountId', expenseAccounts, { 'data-help': 'fixedAssets.set.field.expense' })}
        {picker('Default gain-on-disposal account', 'defaultGainOnDisposalAccountId', revenueAccounts, { 'data-help': 'fixedAssets.set.field.gain' })}
        {picker('Default loss-on-disposal account', 'defaultLossOnDisposalAccountId', expenseAccounts, { 'data-help': 'fixedAssets.set.field.loss' })}
        {picker('Default opening-balance-equity account', 'defaultOpeningBalanceEquityAccountId', equityAccounts, { 'data-help': 'fixedAssets.set.field.openingEquity' })}
        {picker('Revaluation reserve account (equity, e.g. 3002030000)', 'revaluationReserveAccountId', equityAccounts, { 'data-help': 'fixedAssets.set.field.reserve' })}
        {picker('Impairment-loss account (expense)', 'impairmentLossAccountId', expenseAccounts, { 'data-help': 'fixedAssets.set.field.impLoss' })}
        {picker('Accumulated-impairment account (contra asset)', 'accumulatedImpairmentAccountId', assetAccounts, { 'data-help': 'fixedAssets.set.field.accumImp' })}
        {picker('Default funding account (cash, bank or payable)', 'defaultFundingAccountId', fundingAccounts, { 'data-help': 'fixedAssets.set.field.funding' })}
        <Typography variant="caption" color="text.secondary">
          The default funding account is credited when someone without accounting access buys an asset, and debited
          for their disposal proceeds. The opening-balance-equity account is used when registering an existing asset with prior
          accumulated depreciation.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button data-help="fixedAssets.set.save" variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FixedAssetSettings;
